import { AuthHelper } from "@/components/pages/Auth/AuthHelper";
import { UserLoginContext } from "@/components/pages/Auth/UserLoginProvider";
import { NumberFormatCustom } from "@/components/StoreComponents/NumberFormatCustom";
import {
  OutlineButton,
  SolidButton,
} from "@/components/StoreComponents/StoreButton";
import { reloadProfile } from "@/redux/actions";
import { useAppDispatch } from "@/redux/hooks";
import {
  formatPhone,
  getPhoneNumberMasking,
  validatePhone,
} from "@/utils/StringUtils";
import { Box, useTheme } from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { CheckoutInputV2 } from "../../../CheckoutInputV2";
import { ReactComponent as CloseIcon } from "./cancel-close.svg";

const CheckoutSignIn = () => {
  const dispatch = useAppDispatch();

  const theme = useTheme();

  const [otpSession, setOtpSession] =
    useState<{ username: string } | undefined>();
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [verificationCode, setVerificationCode] = useState<string>("");
  const [email, setEmail] = useState<string>("");

  const hasOtpSession = !!otpSession?.username;

  useEffect(() => {
    // Check if the phone number entered is different from the one in the session; if so, clear the session
    if (
      hasOtpSession &&
      AuthHelper.getEmailForPhoneNumber(phoneNumber) !== otpSession.username
    ) {
      setOtpSession(undefined);
    }
  }, [hasOtpSession, otpSession?.username, phoneNumber]);

  const {
    signIn,
    submittingSignIn,
    submittingOtp,
    otpError,
    resendCooldown,
    verifyOtp,
    resendOtp,
    clearOtpError,
  } = useContext(UserLoginContext);

  const submitPhoneNumber = () => {
    const phoneNumberInput = formatPhone(phoneNumber, "e164");
    signIn(phoneNumberInput, false, (_, session) => {
      setOtpSession(session);
    });
  };

  const submitOTP = (otp: string) => {
    verifyOtp(otp, otpSession, () => {
      dispatch(reloadProfile());
    });
  };

  const resendOTP = () => {
    resendOtp(phoneNumber, false, (session) => {
      setOtpSession(session);
    });
  };

  return (
    <div>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "column", md: "row" },
          gap: "16.3px",
          [theme.breakpoints.up("md")]: {
            ".mobileOnly": {
              display: "none",
            },
          },
        }}
      >
        <CheckoutInputV2
          label="Phone Number"
          name="phoneNumber"
          autoComplete="tel"
          value={phoneNumber}
          style={{
            minWidth: "300px",
          }}
          onChange={(e) => {
            const value = e.target.value;
            setPhoneNumber(value?.trim());
            // Clear any previous errors if needed
          }}
          InputProps={{
            inputComponent: NumberFormatCustom as any,
            inputProps: {
              format: getPhoneNumberMasking(phoneNumber || ""),
            },
          }}
          sx={{ flex: 1 }}
        />
        {otpSession ? (
          <>
            <CheckoutInputV2
              label="Verification Code"
              name="verificationCode"
              autoComplete="one-time-code"
              value={verificationCode}
              disabled={!otpSession}
              style={{
                minWidth: "300px",
              }}
              inputStyle={{
                color: otpError ? theme.palette.error.main : "inherit",
              }}
              onChange={(e) => {
                setVerificationCode(e.target.value);
                clearOtpError();
              }}
              sx={{ flex: 1 }}
              endButton={
                otpError ? (
                  <Box
                    sx={{
                      backgroundColor: theme.palette.error.main,
                      width: "26px",
                      height: "26px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "50%",
                    }}
                  >
                    <CloseIcon style={{ paddingLeft: "1px" }} />
                  </Box>
                ) : null
              }
            />
            <OutlineButton
              onClick={() => {
                setVerificationCode("");
                resendOTP();
              }}
              disabled={resendCooldown > 0 || submittingOtp}
            >
              Resend code
            </OutlineButton>
            <SolidButton
              onClick={() => {
                submitOTP(verificationCode);
              }}
              disabled={
                !otpSession ||
                submittingSignIn ||
                submittingOtp ||
                !verificationCode
              }
            >
              {submittingSignIn ? "Saving" : "Save"}
            </SolidButton>
          </>
        ) : (
          <SolidButton
            onClick={() => {
              submitPhoneNumber();
            }}
            disabled={
              submittingSignIn || submittingOtp || !validatePhone(phoneNumber)
            }
          >
            {submittingSignIn ? "Sending" : "Send verification code"}
          </SolidButton>
        )}
      </Box>

      {/* Come back to this */}
      <Box sx={{ display: "none", mt: "16.3px" }}>
        <CheckoutInputV2
          label="Email"
          name="email"
          autoComplete="email"
          value={email}
          disabled={!otpSession}
          style={{
            minWidth: "300px",
          }}
          onChange={(e) => setEmail(e.target.value)}
          sx={{ flex: 1, width: "100%" }}
        />
        <Box display="flex" flex={1} width="100%"></Box>
      </Box>
    </div>
  );
};

export default CheckoutSignIn;
